import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { BexioPostingResult, CreditCheck } from '../../../models';
import { TmsWebBaseService } from '../../base';

/**
 * Bexio accounting posting. Mirrors XeroService so the posting modals only have to choose
 * which service to call, not behave differently once they have.
 *
 * Narrower than Xero's on purpose: no invoice upload/email and no account browsing - only the
 * pushes, the paid read-back and the credit check.
 */
@Injectable({
  providedIn: 'root',
})
export class BexioService extends TmsWebBaseService {
  constructor() {
    super('BexioAccounts');
  }

  // Resolves with the agents whose name matched no Bexio user (those invoices still post, with
  // the connection's default user) so the posting modal can say so.
  postSalesInvoices(ids: string[]): Observable<BexioPostingResult> {
    return this.httpService.post<BexioPostingResult>(`${this.apiUrl}/CreateSalesInvoices`, { ids });
  }

  postCreditNotes(ids: string[]): Observable<void> {
    return this.httpService.post(`${this.apiUrl}/CreateCreditNotes`, { ids });
  }

  postPurchaseInvoices(ids: string[]): Observable<void> {
    return this.httpService.post(`${this.apiUrl}/CreatePurchaseInvoices`, { ids });
  }

  // Ask Bexio for the paid status of the given invoices; the API stores Paid/DatePaid back
  // on each SalesInvoiceHeader, so the caller just reloads the grid afterwards.
  checkPaid(ids: string[]): Observable<void> {
    return this.httpService.post<void>(`${this.apiUrl}/CheckPaid`, { ids });
  }

  // Same shape as Xero's, feeding the same credit check modal. Errors rather than returning an
  // empty ledger when Bexio cannot be read - an empty ledger looks like a customer who owes
  // nothing, which passes.
  getCreditCheck(id: string): Observable<CreditCheck> {
    return this.httpService.get<CreditCheck>(`${this.apiUrl}/CustomerCreditCheck?id=${id}`);
  }
}
